/**
 * Сколько дней до лета (обратный отсчет)
 * ====================================
 * Отображает сколько дней осталось до начала лета.
 * Если уже лето, то отображает сколько осталось до конца лета.
 *
 * TODO Добавить год в заголовок?
 * TODO https://days.to/until/spring
 * TODO Тут можно сделать такую страничку красивую, которую можно на монитор ставить и постоянно крутить.
 *    Чтобы это работало нужен distractions free mode, красивое оформление, full page, возможно переключатель оформления,
 *    но непонятен смысл этого в плане рекламы
 * TODO Дни до праздников, тут целая серия счетчиков может быть
 * TODO Прикол, есть еще астрономическое лето... и метеорологическое лето... в инфо
 *    и вообще зависит от культурных особенностей страны
 *  https://en.wikipedia.org/wiki/Summer
 * TODO Переключатель календарное/метеорологическое/астрономическое
 * TODO Определять страну по геолокации и показывать соответствующее лето
 * TODO Учитывать полушария
 * TODO H2: Почему лето начинается 21 июня?
 * TODO ПРоверить последнюю цифру - считать сегодняшний день или не считать?
 * TODO Adjust font-size based on locale (too long for some)
 * TODO Handle moment of season change
 * TODO Handle southern hermisphere
 *
 * TODO сколько дней до лета без выходных (рабочих дней)  3К
 *      сколько дней до лета без выходных и праздников (800)
 * TODO ключи:
 *  таймер
 *  сколько дней до конца лета 500
 */
import { LoaderFunctionArgs, MetaFunction, useLoaderData, useLocation } from "react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import invariant from "tiny-invariant";

import ArticleImage from "~/components/ArticleImage";
import FullCountdown from "~/components/days-till/FullCountdown";
import H2 from "~/components/H2";
import AdditionalInfo from "~/components/tool-page/AdditionalInfo";
import AdditionalInfoSection from "~/components/tool-page/AdditionalInfoSection";
import result from "~/components/tool-page/Result";
import { createHydrationSafeDiff, getDatesDiff } from "~/helpers/dates";
import { celebrities } from "~/helpers/numbers/age.server";
import { makeTitle } from "~/helpers/pageTitle";
import { createWebAppSchema, createFAQSchema } from "~/helpers/schemaOrg";
import { getLocale } from "~/i18n";
import { getTranslator } from "~/i18next.server";
import CalculatorLayout from "~/routes/layouts/components/CalculatorLayout";
import AdditionalContentLayout from "~/routes/layouts/components/AdditionalContentLayout";
import ToolHeaderNew from "~/components/tool-page/ToolHeaderNew";
import { DateDiff } from "~/helpers/dates.server";

const CATEGORY = "dates";
const TKEY = "summer";
const ROUTE_ENDING = "how-many-days-till/summer";

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const result = [
    { title: makeTitle(data?.meta.title) },
    { name: "description", content: data?.meta.description },
    createWebAppSchema(
      data?.meta.locale as string,
      data?.meta.title as string,
      []
    )
  ];
  const faqSchema = createFAQSchema(data?.faq ?? []);
  if (faqSchema) result.push(faqSchema);
  return result;
};

export const handle = {
  localisedLinks: ["en", "ru", "de", "fr", "es", "it", "pt"],
  i18n: ["days-till-summer"],
};

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const locale = getLocale(request.url) ?? "";

  const t = await getTranslator(request, "days-till-summer");

  const breadcrumbs = [
    { title: `catalog.categories.${CATEGORY}`, to: `${CATEGORY}` },
    { title: `calculators.dates.days_till.summer.title`, to: `${CATEGORY}/${ROUTE_ENDING}` }
  ];

  const faq = [
    { q: t(`${TKEY}.info_why_change_h`), a: t(`${TKEY}.info_why_change_p1`) },
    { q: t(`${TKEY}.info_get_precautions_h`), a: t(`${TKEY}.info_get_precautions_p`) },
    { q: t(`${TKEY}.info_hermispheres_h`), a: t(`${TKEY}.info_hermispheres_p`) },
  ];

  return {
    result,
    celebrities: celebrities[locale] ?? [],
    faq,
    meta: {
      locale,
      title: t(`${TKEY}.title`),
      description: t(`${TKEY}.description`),
      breadcrumbs
    }
  };
};

export default function Index() {
  const {
    meta
  } = useLoaderData<typeof loader>();

  const { t } = useTranslation("days-till-summer");
  const locale = getLocale(useLocation().pathname);
  invariant(locale);

  const currentDate = new Date();
  const currentMonth = currentDate.getMonth();
  const isItNow = currentMonth >= 5 && currentMonth <= 7;

  const nextStartDate = isItNow || currentMonth > 7
    ? new Date(currentDate.getFullYear() + 1, 5, 1)
    : new Date(currentDate.getFullYear(), 5, 1)
  ;

  const endDate = isItNow ? new Date(currentDate.getFullYear(), 8, 1) : null;

  // Create a date with seconds/milliseconds zeroed out for consistent initial render
  const safeDate = new Date();
  safeDate.setSeconds(0, 0);
  
  // Calculate initial diffs that will be consistent between server and client
  const initialDiff = getDatesDiff(safeDate, nextStartDate);
  const initialEndDiff = endDate ? getDatesDiff(safeDate, endDate) : null;
  
  // Make the initial diffs safe for hydration by zeroing out hours, minutes, seconds
  const safeDiff = createHydrationSafeDiff(initialDiff);
  const safeEndDiff = initialEndDiff ? createHydrationSafeDiff(initialEndDiff) : null;
  
  // State for rendering
  const [diff, setDiff] = useState<DateDiff>(safeDiff);
  const [endDiff, setEndDiff] = useState<DateDiff | null>(safeEndDiff);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    // Mark that we're hydrated
    setHydrated(true);
    
    // Update with real-time data after hydration
    const updateDiffs = () => {
      const now = new Date();
      setDiff(getDatesDiff(now, nextStartDate));
      if (endDate) {
        setEndDiff(getDatesDiff(now, endDate));
      }
    };
    
    // Initial update
    updateDiffs();
    
    // Set up interval for live updates
    const interval = setInterval(updateDiffs, 1000);

    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <CalculatorLayout>
        <ToolHeaderNew
          name={meta.title}
          description={meta.description}
        />

        <FullCountdown
          isItNow={isItNow}
          endDiff={endDiff}
          diff={diff}
          nextStartDate={nextStartDate}
          t={t}
          tkey={TKEY}
          hydrated={hydrated}
        />
      </CalculatorLayout>
      
      <AdditionalContentLayout>
        <AdditionalInfo>
          <p>{t(`${TKEY}.info_intro_p`)}</p>

          <ArticleImage
            src={`https://static.countlike.pro/summer-in-moscow-russia.webp`}
            alt={t(`${TKEY}.image_caption`)}
            caption={t(`${TKEY}.image_caption`)}
            width={1200}
            height={686}
          />

          <AdditionalInfoSection id="starts" contentsText={t(`${TKEY}.info_starts_h`)} contentsEmoji="🌅">
            <H2>{t(`${TKEY}.info_starts_h`)}</H2>
            <p dangerouslySetInnerHTML={{ __html: t(`${TKEY}.info_starts_calendar`) }} />
            <p dangerouslySetInnerHTML={{ __html: t(`${TKEY}.info_starts_meteo`) }} />
            <p dangerouslySetInnerHTML={{ __html: t(`${TKEY}.info_starts_astro`) }} />
          </AdditionalInfoSection>

          <AdditionalInfoSection id="ends" contentsText={t(`${TKEY}.info_ends_h`)} contentsEmoji="❄️">
            <H2>{t(`${TKEY}.info_ends_h`)}</H2>
            <p dangerouslySetInnerHTML={{ __html: t(`${TKEY}.info_ends_calendar`) }} />
            <p dangerouslySetInnerHTML={{ __html: t(`${TKEY}.info_ends_meteo`) }} />
            <p dangerouslySetInnerHTML={{ __html: t(`${TKEY}.info_ends_astro`) }} />
          </AdditionalInfoSection>

          <AdditionalInfoSection id="solstice" contentsText={t(`${TKEY}.info_solstice_h`)} contentsEmoji="☀️">
            <H2>{t(`${TKEY}.info_solstice_h`)}</H2>
            <p dangerouslySetInnerHTML={{ __html: t(`${TKEY}.info_solstice_p`) }} />
          </AdditionalInfoSection>

          <AdditionalInfoSection id="equinox" contentsText={t(`${TKEY}.info_equinox_h`)} contentsEmoji="🍂">
            <H2>{t(`${TKEY}.info_equinox_h`)}</H2>
            <p dangerouslySetInnerHTML={{ __html: t(`${TKEY}.info_equinox_p`) }} />
          </AdditionalInfoSection>

          <AdditionalInfoSection id="hemispheres" contentsText={t(`${TKEY}.info_hermispheres_h`)} contentsEmoji="🌍">
            <H2>{t(`${TKEY}.info_hermispheres_h`)}</H2>
            <p>{t(`${TKEY}.info_hermispheres_p`)}</p>
          </AdditionalInfoSection>

          <AdditionalInfoSection id="why-change" contentsText={t(`${TKEY}.info_why_change_h`)} contentsEmoji="❓">
            {/*TODO https://youtu.be/1fX-JR9GcxY*/}
            <H2>{t(`${TKEY}.info_why_change_h`)}</H2>
            <p dangerouslySetInnerHTML={{ __html: t(`${TKEY}.info_why_change_p1`) }} />
            {/* TODO A good place to embed a video */}
          </AdditionalInfoSection>

          <AdditionalInfoSection id="facts" contentsText={t(`${TKEY}.info_facts_h`)} contentsEmoji="📚">
            <H2>{t(`${TKEY}.info_facts_h`)}</H2>
            <ul className="list-inside flex flex-col gap-4">
              <li dangerouslySetInnerHTML={{ __html: t(`${TKEY}.info_facts_1`) }} />
              <li dangerouslySetInnerHTML={{ __html: t(`${TKEY}.info_facts_2`) }} />
              <li dangerouslySetInnerHTML={{ __html: t(`${TKEY}.info_facts_3`) }} />
              <li dangerouslySetInnerHTML={{ __html: t(`${TKEY}.info_facts_4`) }} />
              <li dangerouslySetInnerHTML={{ __html: t(`${TKEY}.info_facts_5`) }} />
              <li dangerouslySetInnerHTML={{ __html: t(`${TKEY}.info_facts_6`) }} />
            </ul>
          </AdditionalInfoSection>

          {locale === "ru" ? (
            <AdditionalInfoSection id="babye-leto" contentsText={t(`${TKEY}.info_babye_h`)} contentsEmoji="🍁">
              <H2>{t(`${TKEY}.info_babye_h`)}</H2>
              <p>{t(`${TKEY}.info_babye_p`)}</p>
            </AdditionalInfoSection>
          ) : null}

          <AdditionalInfoSection id="importance" contentsText={t(`${TKEY}.info_importance_h`)} contentsEmoji="⚠️">
            <H2>{t(`${TKEY}.info_importance_h`)}</H2>
            <ul className="list-inside flex flex-col gap-4">
              <li>{t(`${TKEY}.info_importance_list_1`)}</li>
              <li>{t(`${TKEY}.info_importance_list_2`)}</li>
              <li>{t(`${TKEY}.info_importance_list_3`)}</li>
              <li>{t(`${TKEY}.info_importance_list_4`)}</li>
            </ul>
          </AdditionalInfoSection>

          <AdditionalInfoSection id="get-ready" contentsText={t(`${TKEY}.info_get_ready_h`)} contentsEmoji="💪">
            <H2>{t(`${TKEY}.info_get_ready_h`)}</H2>
            <p>{t(`${TKEY}.info_get_ready_p`)}</p>
          </AdditionalInfoSection>

          <AdditionalInfoSection id="precautions" contentsText={t(`${TKEY}.info_get_precautions_h`)} contentsEmoji="🧴">
            <H2>{t(`${TKEY}.info_get_precautions_h`)}</H2>
            <p>{t(`${TKEY}.info_get_precautions_p`)}</p>
            <p>{t(`${TKEY}.info_get_precautions_p2`)}</p>
            <p>{t(`${TKEY}.info_get_precautions_p3`)}</p>
            <ul className="list-inside flex flex-col gap-4">
              <li dangerouslySetInnerHTML={{ __html: t(`${TKEY}.info_get_precautions_1`) }} />
              <li dangerouslySetInnerHTML={{ __html: t(`${TKEY}.info_get_precautions_2`) }} />
              <li dangerouslySetInnerHTML={{ __html: t(`${TKEY}.info_get_precautions_3`) }} />
              <li dangerouslySetInnerHTML={{ __html: t(`${TKEY}.info_get_precautions_4`) }} />
              <li dangerouslySetInnerHTML={{ __html: t(`${TKEY}.info_get_precautions_5`) }} />
              <li dangerouslySetInnerHTML={{ __html: t(`${TKEY}.info_get_precautions_6`) }} />
              <li dangerouslySetInnerHTML={{ __html: t(`${TKEY}.info_get_precautions_7`) }} />
              <li dangerouslySetInnerHTML={{ __html: t(`${TKEY}.info_get_precautions_8`) }} />
              <li dangerouslySetInnerHTML={{ __html: t(`${TKEY}.info_get_precautions_9`) }} />
              <li dangerouslySetInnerHTML={{ __html: t(`${TKEY}.info_get_precautions_10`) }} />
            </ul>
          </AdditionalInfoSection>

          <AdditionalInfoSection id="enjoy" contentsText={t(`${TKEY}.info_enjoy_h`)} contentsEmoji="😀">
            <H2>{t(`${TKEY}.info_enjoy_h`)}</H2>
            <p>{t(`${TKEY}.info_enjoy_p`)}</p>
          </AdditionalInfoSection>
        </AdditionalInfo>
      </AdditionalContentLayout>
    </>
  );
}


